import { useParams } from "react-router-dom";
import { useOrganization } from "../context/OrganizationContext";
import { useFirebaseDoc, useFirebaseQuery } from "../hooks/firebaseHooks";
import { Deal } from "../models/shared/Deal";
import { TimeRecord } from "../models/shared/TimeRecord";
import { Offer } from "../models/shared/Offer";
import { OfferAuditRecordType } from "../models/shared/OfferAuditRecord";
import { COLLECTIONS } from "../utils/shared/constants";
import PageHeader from '../components/PageHeader';
import MapDealView from '../components/MapDealView';
import { useText } from '../context/TextContext';
import { useUser } from '../context/UserContext';
import { where } from "firebase/firestore";
import ActionLog, { ActionLogItem } from '../components/ActionLog';
import { renderAuditMessage } from '../utils/presentation';
import { formatDuration } from '../utils/date';
import { useState } from 'react';
import { doc, deleteDoc } from 'firebase/firestore';
import { useFirestore } from 'reactfire';
import TimesheetEntryModal from '../components/modals/TimesheetEntryModal';
import { toast } from 'react-toastify';
import DealLink from "../components/DealLink";
import useConfirmModal from "../hooks/useConfirmModal";

export default function DealTimeLinePage() {
    const { orgId, orgRole, organization } = useOrganization();
    const { dealId } = useParams<{ dealId: string }>();
    const text = useText();
    const { user } = useUser();
    const [deal] = useFirebaseDoc<Deal>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}`);
    const [dealTimeRecords] = useFirebaseQuery<TimeRecord>(
        `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.TIME_RECORDS}`,
        where("dealId", "==", dealId)
    );
    const [offers] = useFirebaseQuery<Offer>(
        `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}/${COLLECTIONS.OFFERS}`
    );
    const [editingRecord, setTimeRecordToEdit] = useState<TimeRecord | null>(null);
    const firestore = useFirestore();
    const [ConfirmModal, confirm] = useConfirmModal(text.deleteTimeRecord);

    const canEditOrDelete = (tr: TimeRecord) => orgRole === 'admin' || tr.email === user?.email;

    const handleDeleteTimeRecord = async (tr: TimeRecord) => {
        if (!canEditOrDelete(tr)) {
            toast.error(text.defaultError);
            return;
        }

        confirm(async () => {
            try {
                await deleteDoc(doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.TIME_RECORDS}/${tr.id}`));
                toast.success(text.defaultSuccess);
            } catch {
                toast.error(text.defaultError);
            }
        });
    };

    const filteredTimeRecords = (orgRole === 'worker')
        ? user?.email
            ? dealTimeRecords.filter(tr => tr.email === user.email)
            : []
        : dealTimeRecords;
    const timeRecordActions: ActionLogItem[] = filteredTimeRecords.map(tr => ({
        text: `${text.hoursTracked}: ${formatDuration(tr.startAt, tr.endAt)}`,
        date: tr.startAt || new Date(0),
        email: tr.email,
        onEdit: canEditOrDelete(tr) ? () => setTimeRecordToEdit(tr) : undefined,
        onDelete: () => handleDeleteTimeRecord(tr)

    }));

    const auditRecords = deal?.auditRecords ? [...deal.auditRecords] : [];

    const auditActions: ActionLogItem[] = auditRecords.map(record => ({
        text: renderAuditMessage(record, organization, text),
        date: record.createdAt
    }));

    const noteActions: ActionLogItem[] = (deal?.notes || []).map(note => ({
        text: note.content,
        date: note.createdAt,
        email: note.createdBy,
    }));

    const offerActions: ActionLogItem[] = offers.flatMap(offer => {
        const items: ActionLogItem[] = [
            { text: `${text.offer}: ${text.offerCreated}`, date: offer.createdAt },
        ];
        for (const record of offer.auditRecords || []) {
            let recordText = '';
            if (record.type === OfferAuditRecordType.Visited) recordText = text.offerOpenedByCustomer;
            else if (record.type === OfferAuditRecordType.Accepted) recordText = text.offerAccepted;
            else if (record.type === OfferAuditRecordType.Comment) recordText = `${text.customerLeftComment}: ${record.extras?.comment || ''}`;
            if (recordText) items.push({ text: recordText, date: record.createdAt });
        }
        return items;
    });

    const allActions = [...timeRecordActions, ...auditActions, ...noteActions, ...offerActions];

    return (
        <div>
            <PageHeader
                title={`${deal?.name} - ${text.timeline}`}
                showBackButton
            />

            <DealLink dealId={dealId || ''} label={<div className="d-flex gap-2">
                <div>{text.dealDetails}</div> <i className="bi bi-box-arrow-up-right"></i>
            </div>} />
            <h6>{deal?.description}</h6>
            <span className="small">{deal?.place?.name}</span>

            {deal?.place && (
                <div>
                    <MapDealView readonly
                        placeGeocoding={deal.place.geocoding}
                        onPlaceChange={() => Promise.resolve()} />
                </div>
            )}

            <hr />
            <h6>{text.timeRecords}</h6>
            <ActionLog actions={allActions} />
            {editingRecord && (
                <TimesheetEntryModal
                    onClose={() => setTimeRecordToEdit(null)}
                    initialData={editingRecord}
                />
            )}
            <ConfirmModal />
        </div>
    );
}
